import { gql, useQuery, useLocation } from '@common/hooks';
import React, { useEffect, useMemo, useState } from 'react';
import { TextSearchTableFilter } from 'works/components/filtered-table/text-search-table-filter';
import {
  BuilderConfig,
  filterableColumnBuilder,
} from 'works/legacy/lib/table-filters-helper';
import { FilteredTable } from 'works/components/filtered-table';
import {
  localStorageStore,
  useStoredFilters,
} from 'works/legacy/lib/hooks/use-stored-filters';
import { useSetBreadcrumbs } from 'works/legacy/lib/client-store';
import {
  AddButton,
  Animation,
  AppContent,
  Icon,
  Link,
} from '@common/components';
import { OrganizationUnitScope } from '@institutions/graphql';
import { Button, Dropdown } from 'antd';
import { ORGANIZATION_ADMIN_MENU } from 'works/pages/organization/admin-settings/organization-admin-vertical-nav-menu';
import { buildDialogUrl, scrollIntoElementId } from '@common/services';
import { useHasFilters } from 'works/legacy/lib/hooks/use-has-filters';

const LOCALSTORAGE_FILTER_KEY = 'admin-facilities-unit-filter';

const FACILITIES_AND_UNITS_QUERY = gql`
  query FacilitiesAndUnitsQuery(
    $orgUnitInput: OrganizationUnitsInput
    $orgBusinessesInput: OrganizationBusinessesInput
  ) {
    organizationBusinesses(input: $orgBusinessesInput) {
      id
      name
      organizationUnits(input: $orgUnitInput) {
        id
        name
        costCenter
        clinicalUnit {
          id
          displayName
        }
      }
    }
  }
`;

type FacilityUnit = {
  id: string;
  name: string;
  organizationUnits: {
    id: string;
    name: string;
    costCenter: string;
    clinicalUnit: {
      id: string;
      displayName: string;
    };
  }[];
};

type FacilityUnitResult = {
  organizationBusinesses: FacilityUnit[];
};

type FacilityUnitVariables = {
  orgBusinessesInput: {
    filters: {
      businessName?: string[];
    };
  };
  orgUnitInput: {
    filters: {
      unitName?: string[];
      clinicalUnitDisplayName?: string[];
      costCenter?: string[];
    };
    scope?: OrganizationUnitScope;
  };
};

type ColumnData = {
  id: string;
  name?: string;
  unitName?: string;
  costCenter?: string;
  clinicalUnit?: string;
  children?: ColumnData[];
};

type Filters = {
  name?: string[];
  unitName?: string[];
  clinicalUnit?: string[];
  costCenter?: string[];
};

function mapFilters(filters: Filters): FacilityUnitVariables {
  return {
    orgBusinessesInput: {
      filters: {
        businessName: filters.name,
      },
    },
    orgUnitInput: {
      filters: {
        unitName: filters.unitName,
        clinicalUnitDisplayName: filters.clinicalUnit,
        costCenter: filters.costCenter,
      },
      scope: 'organization',
    },
  };
}

function getColumns(builderConfig: BuilderConfig<ColumnData>) {
  const column = filterableColumnBuilder(builderConfig);

  return [
    column({
      title: 'Facility',
      key: 'name',
      width: '25%',
      sorter: false,
      filterDropdown: TextSearchTableFilter,
      render: (value, record) => (
        <Link
          id={record.id}
          to={`/works/admin/organization-structure/facility/${record.id}/details`}
          className="text-heavy"
        >
          {value}
        </Link>
      ),
    }),
    column({
      title: 'Unit Name',
      key: 'unitName',
      width: '25%',
      sorter: false,
      filterDropdown: TextSearchTableFilter,
      render: (value, record) => (
        <Link
          id={record.id}
          to={`/works/admin/organization-structure/unit/${record.id}`}
          className="text-heavy"
        >
          {value}
        </Link>
      ),
    }),
    column({
      title: 'Unit Type',
      key: 'clinicalUnit',
      width: '25%',
      sorter: false,
      filterDropdown: TextSearchTableFilter,
    }),
    column({
      title: 'Cost Center',
      key: 'costCenter',
      width: '25%',
      sorter: false,
      filterDropdown: TextSearchTableFilter,
    }),
  ];
}

export function AdminOrganizationStructurePage() {
  const { state } = useLocation<{
    organizationBusinessId: string;
    organizationUnitsIds: string[];
  }>();
  const initiallyOpenOrganizationBusinessId = state?.organizationBusinessId;
  const recentlyAddedOrganizationUnits = useMemo(
    () => state?.organizationUnitsIds || [],
    [state]
  );

  const [expandedRowKeys, setExpandedRowKeys] = useState([
    initiallyOpenOrganizationBusinessId,
  ]);

  useEffect(() => {
    if (!recentlyAddedOrganizationUnits.length) {
      return;
    }

    scrollIntoElementId({
      elementId: recentlyAddedOrganizationUnits[0],
      delayInSeconds: 3,
    });
  }, [recentlyAddedOrganizationUnits]);

  useSetBreadcrumbs([
    {
      name: 'Admin',
      path: '/works/admin',
    },
    {
      name: ORGANIZATION_ADMIN_MENU.LOCATIONS.LABEL,
      path: ORGANIZATION_ADMIN_MENU.LOCATIONS.ROUTE,
    },
  ]);

  const [selectedFilters, setSelectedFilters] = useStoredFilters(
    LOCALSTORAGE_FILTER_KEY,
    {
      store: localStorageStore,
      defaults: {} as Filters,
    }
  );

  function onExpand(expanded: boolean, key: string) {
    if (expanded) {
      setExpandedRowKeys(expandedRowKeys.filter((k) => k !== key));
    } else {
      setExpandedRowKeys([...expandedRowKeys, key]);
    }
  }

  function expandAll(keys: string[]) {
    setExpandedRowKeys(keys);
  }

  function clearExpand() {
    setExpandedRowKeys([]);
  }

  const { data, loading, fetchMore } = useQuery<
    FacilityUnitResult,
    FacilityUnitVariables
  >(FACILITIES_AND_UNITS_QUERY, {
    variables: mapFilters(selectedFilters),
  });

  const columns = getColumns({
    paginated: false,
    filterConfig: {
      selectedFilters,
    },
  });

  const hasFilters = useHasFilters(selectedFilters);

  const viewData: ColumnData[] = useMemo(() => {
    if (!data?.organizationBusinesses || loading) {
      return [];
    }

    const tableData = data.organizationBusinesses.map((ob) => ({
      id: ob.id,
      name: ob.name,
      children: ob.organizationUnits?.map((ou) => ({
        id: ou.id,
        unitName: ou.name,
        costCenter: ou.costCenter,
        clinicalUnit: ou.clinicalUnit.displayName,
      })),
    }));

    if (hasFilters) {
      const withChildrenTableData = tableData.filter(
        function filterEmptyChildren(business) {
          return business?.children.length > 0;
        }
      );

      expandAll(withChildrenTableData.map((x) => x.id));

      return withChildrenTableData;
    } else if (!initiallyOpenOrganizationBusinessId) {
      clearExpand();
    }

    return tableData;
  }, [data, loading, initiallyOpenOrganizationBusinessId, hasFilters]);

  function handleTableChange(
    _pagination: object,
    filters: {
      [key: string]: [string];
    }
  ) {
    fetchMore({
      variables: mapFilters(filters),
      updateQuery(previousQueryResult, { fetchMoreResult }) {
        if (!fetchMoreResult) return previousQueryResult;

        return fetchMoreResult;
      },
    });
  }

  return (
    <>
      <AppContent className="admin-organization-structure-page">
        <FilteredTable
          filters={selectedFilters}
          onChange={handleTableChange}
          onFilterChange={setSelectedFilters}
          localStorageFilterKey={LOCALSTORAGE_FILTER_KEY}
          pagination={{
            hideOnSinglePage: true,
            pageSize: viewData.length,
            total: viewData.length,
          }}
          loading={loading}
          columns={columns}
          dataSource={viewData}
          rowKey="id"
          scroll={{ x: '100%' }}
          expandable={{
            expandRowByClick: false,
            expandedRowKeys,
            indentSize: 0,
            defaultExpandAllRows: true,
            expandIcon: ({
              expanded,
              record,
            }: {
              expanded: boolean;
              record: ColumnData;
            }) => {
              if (!record.children?.length)
                return <span style={{ marginLeft: 26 }} />;

              return (
                <ToggleButton
                  expanded={expanded}
                  onClick={() => onExpand(expanded, record.id)}
                />
              );
            },
          }}
          rowClassName={(record: { id: string }) =>
            recentlyAddedOrganizationUnits.includes(record.id) &&
            'highlight-recently-updated'
          }
          headerRightSlot={<CreationUnitFacilityMenu />}
          onRow={(record: ColumnData) => ({
            onClick: record.name
              ? () => onExpand(expandedRowKeys.includes(record.id), record.id)
              : null,
          })}
        />
      </AppContent>
      <style jsx>{`
        :global(
          .admin-organization-structure-page .ant-table-filter-column-title
        ) {
          padding: 0;
        }

        :global(.admin-organization-structure-page .ant-table-filter-column) {
          margin: 0;
        }
        :global(.admin-organization-structure-page .ant-table-tbody > tr > td) {
          padding: 14px 4px 14px 8px;
        }
      `}</style>
    </>
  );
}

function CreationUnitFacilityMenu() {
  const dropdownMenuItems = [
    {
      label: (
        <Link to={`${ORGANIZATION_ADMIN_MENU.LOCATIONS.ROUTE}/add-units`}>
          <Icon icon="hospitals" kind="light" /> Unit
        </Link>
      ),
      key: 'unit',
    },
    {
      label: (
        <Link to={buildDialogUrl('organization-facility-add-drawer')}>
          <Icon icon="hospitalWide" kind="light" /> Facility
        </Link>
      ),
      key: 'facility',
    },
  ];

  return (
    <Dropdown
      menu={{
        items: dropdownMenuItems,
      }}
      placement="bottomRight"
    >
      <AddButton showDropdown>New</AddButton>
    </Dropdown>
  );
}

type ToggleButtonProps = {
  expanded: boolean;
  onClick: () => void;
};

const ToggleButton = ({ expanded, onClick }: ToggleButtonProps) => (
  <span className="toggle-button">
    <Button
      type="link"
      className="admin-organization-structure-page-toggle-button"
      onClick={onClick}
    >
      <Animation kind="rotate-left" active={expanded}>
        <Icon icon="chevronDown" />
      </Animation>
    </Button>
    <style jsx>{`
      .toggle-button :global(.ant-btn) {
        padding: 0 8px 0 0;
        height: 16px;
      }
      .toggle-button :global(.wk-animation) {
        line-height: 0;
        vertical-align: top;
      }
    `}</style>
  </span>
);
